import { useEffect, type RefObject } from 'react'
import { isEditableTarget } from '@/hooks/use-hotkey'
import { tickerSearchOpenEvent } from './ticker-search-events'

/** The panel's outer frame: a mosaic window, or a tile in grid layout mode. */
const PANEL_FRAME_SELECTOR = '.mosaic-window, [data-grid-panel]'

/** The ticker-search combobox trigger inside the anchor (the header pill). */
const TICKER_SEARCH_TRIGGER_SELECTOR = '[data-companion-label$="ticker-search"]'

/**
 * Focus inside one of these owns its own letters: Radix menus and selects
 * typeahead on them, and dialogs are a separate context. Portaled, so the
 * hover check alone can't rule them out.
 */
const POPUP_SELECTOR =
  '[role="menu"], [role="listbox"], [role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper]'

/**
 * The seed a keystroke would type into the ticker search, or null when the key
 * isn't a type-to-search key. Mirrors the chart's bare-letter rules
 * (ChartGrid's ticker-search keydown handler): a single letter, Shift allowed,
 * no Cmd/Ctrl/Alt chord, no auto-repeat, not already claimed by another
 * listener, and never while typing in a field or a popup.
 */
export function typeToTickerSearchSeed(e: KeyboardEvent): string | null {
  if (!/^[a-zA-Z]$/.test(e.key)) return null
  if (e.repeat || e.isComposing || e.metaKey || e.ctrlKey || e.altKey) return null
  if (e.defaultPrevented) return null
  if (isEditableTarget(e.target)) return null
  if (e.target instanceof Element && e.target.closest(POPUP_SELECTOR)) return null
  return e.key.toUpperCase()
}

export interface TypeToTickerSearchOptions {
  /** False for hosts that don't own the gesture (chart-family panels: ChartGrid does). */
  enabled: boolean
  /**
   * An element holding the panel's ticker search (the panel header). Its
   * closest mosaic window / grid tile is the panel the pointer must be over.
   */
  anchorRef: RefObject<HTMLElement | null>
}

/**
 * Type-to-search for a panel's ticker search, the chart's gesture generalized:
 * with the pointer over the panel, typing a bare letter opens the panel's
 * ticker search with that letter already in the input.
 *
 * Hover-gated, like every per-panel hotkey here (see usePanelHover and
 * useTagShortcut): there is no workspace-wide active-panel store, and hover
 * picks exactly one panel, so a keystroke never opens two searches. The frame's
 * own mouseenter/mouseleave track the pointer, with `:hover` as the fallback
 * for a panel that (re)mounted under a stationary cursor.
 */
export function useTypeToTickerSearch({ enabled, anchorRef }: TypeToTickerSearchOptions): void {
  useEffect(() => {
    const anchor = anchorRef.current
    if (!enabled || !anchor) return
    const frame = anchor.closest<HTMLElement>(PANEL_FRAME_SELECTOR) ?? anchor
    let hovered = false
    const onEnter = () => {
      hovered = true
    }
    const onLeave = () => {
      hovered = false
    }
    const isHovered = () => {
      if (hovered) return true
      try {
        return frame.matches(':hover')
      } catch {
        // Selector engines without user-action pseudo-classes (jsdom).
        return false
      }
    }
    const onKeyDown = (e: KeyboardEvent) => {
      const seed = typeToTickerSearchSeed(e)
      if (!seed || !isHovered()) return
      const trigger = anchor.querySelector<HTMLElement>(TICKER_SEARCH_TRIGGER_SELECTOR)
      if (!trigger) return
      e.preventDefault()
      trigger.dispatchEvent(tickerSearchOpenEvent(seed))
    }
    frame.addEventListener('mouseenter', onEnter)
    frame.addEventListener('mouseleave', onLeave)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      frame.removeEventListener('mouseenter', onEnter)
      frame.removeEventListener('mouseleave', onLeave)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [enabled, anchorRef])
}
